/**
 * Client-side filter/sort helpers for public (and shared) build explorers.
 */

export type PublicBuildView = 'list' | 'timeline' | 'card' | 'explore';

export interface PublicBuildMod {
  id?: string | number;
  title: string;
  description?: string | null;
  category?: string;
  vendor?: string;
  cost?: number | null;
  install_date?: string | null;
  notes?: string | null;
  parent_modification_id?: string | number | null;
  parentComponent?: string | null;
  specifications?: string[] | Record<string, unknown> | string | null;
  sort_order?: number | null;
  image_url?: string | null;
  image_id?: number | null;
}

export interface PublicBuildFilters {
  search: string;
  categories: string[]; // empty or ['All'] = all
  brands: string[]; // empty or ['All'] = all
  sort: 'name' | 'category' | 'brand' | 'newest' | 'order';
}

function specsHaystack(specifications: PublicBuildMod['specifications']): string {
  if (!specifications) return '';
  if (typeof specifications === 'string') return specifications;
  if (Array.isArray(specifications)) return specifications.join(' ');
  if (typeof specifications === 'object') {
    return Object.entries(specifications)
      .map(([k, v]) => `${k} ${v}`)
      .join(' ');
  }
  return '';
}

export function matchesPublicBuildFilters(
  mod: PublicBuildMod,
  filters: Pick<PublicBuildFilters, 'search' | 'categories' | 'brands'>
): boolean {
  const cats = filters.categories;
  const brands = filters.brands;
  const catOk =
    !cats.length ||
    cats.includes('All') ||
    (mod.category ? cats.includes(mod.category) : false);
  const brandOk =
    !brands.length ||
    brands.includes('All') ||
    (mod.vendor ? brands.includes(mod.vendor) : brands.includes('Unbranded'));
  if (!catOk || !brandOk) return false;

  const q = filters.search.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    mod.title,
    mod.description || '',
    mod.notes || '',
    mod.category || '',
    mod.vendor || '',
    mod.parentComponent || '',
    specsHaystack(mod.specifications),
  ]
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

function sortOrderKey(m: PublicBuildMod): number {
  return m.sort_order ?? 0;
}

export function sortPublicBuildMods(
  mods: PublicBuildMod[],
  sort: PublicBuildFilters['sort']
): PublicBuildMod[] {
  if (sort === 'order') {
    return [...mods];
  }
  const copy = [...mods];
  copy.sort((a, b) => {
    if (sort === 'category') {
      const c = (a.category || '').localeCompare(b.category || '', undefined, {
        sensitivity: 'base',
      });
      if (c !== 0) return c;
      const so = sortOrderKey(a) - sortOrderKey(b);
      if (so !== 0) return so;
      return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    }
    if (sort === 'brand') {
      const c = (a.vendor || '').localeCompare(b.vendor || '', undefined, {
        sensitivity: 'base',
      });
      if (c !== 0) return c;
      const so = sortOrderKey(a) - sortOrderKey(b);
      if (so !== 0) return so;
      return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    }
    if (sort === 'newest') {
      const da = a.install_date ? new Date(a.install_date).getTime() : 0;
      const db = b.install_date ? new Date(b.install_date).getTime() : 0;
      if (db !== da) return db - da;
      return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    }
    const so = sortOrderKey(a) - sortOrderKey(b);
    if (so !== 0) return so;
    return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
  });
  return copy;
}

export function filterAndSortPublicBuild(
  mods: PublicBuildMod[],
  filters: PublicBuildFilters
): PublicBuildMod[] {
  return sortPublicBuildMods(
    mods.filter((m) => matchesPublicBuildFilters(m, filters)),
    filters.sort
  );
}

export function uniqueCategories(mods: PublicBuildMod[]): string[] {
  const set = new Set<string>();
  mods.forEach((m) => {
    if (m.category) set.add(m.category);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function uniqueBrands(mods: PublicBuildMod[]): string[] {
  const set = new Set<string>();
  let hasUnbranded = false;
  mods.forEach((m) => {
    if (m.vendor) set.add(m.vendor);
    else hasUnbranded = true;
  });
  const list = Array.from(set).sort((a, b) => a.localeCompare(b));
  if (hasUnbranded) list.push('Unbranded');
  return list;
}

/** Map legacy view query values onto the current tab set. */
export function normalizePublicBuildView(viewRaw: string | null): PublicBuildView {
  if (!viewRaw) return 'card';
  if (viewRaw === 'compact') return 'list';
  // Former Build Sheet / Categories / hierarchy → Cards
  if (
    viewRaw === 'sheet' ||
    viewRaw === 'hierarchy' ||
    viewRaw === 'categories' ||
    viewRaw === 'compact-tree'
  ) {
    return 'card';
  }
  const valid: PublicBuildView[] = ['list', 'timeline', 'card', 'explore'];
  return (valid as string[]).includes(viewRaw) ? (viewRaw as PublicBuildView) : 'card';
}

export function parsePublicBuildQuery(search: string): {
  view: PublicBuildView;
  category: string | null;
  brand: string | null;
  q: string;
  /** Focused modification id for Explore view */
  focus: string | null;
  /** Car Show Mode presentation */
  carShow: boolean;
  /** Legacy ?view=compact should open list + export */
  openExport?: boolean;
} {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const viewRaw = params.get('view');
  const openExport = viewRaw === 'compact';
  const carShowRaw = params.get('carshow') ?? params.get('car_show');
  const carShow =
    carShowRaw !== null &&
    ['', '1', 'true', 'yes', 'carshow'].includes(carShowRaw.trim().toLowerCase());
  return {
    view: normalizePublicBuildView(viewRaw),
    category: params.get('category'),
    brand: params.get('brand'),
    q: params.get('q') || '',
    focus: params.get('focus'),
    carShow,
    openExport,
  };
}

export function buildPublicBuildQuery(state: {
  view: PublicBuildView;
  category?: string | null;
  brand?: string | null;
  q?: string;
  focus?: string | null;
  carShow?: boolean;
}): string {
  const params = new URLSearchParams();
  if (state.view !== 'card') params.set('view', state.view);
  if (state.category) params.set('category', state.category);
  if (state.brand) params.set('brand', state.brand);
  if (state.q?.trim()) params.set('q', state.q.trim());
  if (state.focus?.trim()) params.set('focus', state.focus.trim());
  if (state.carShow) params.set('carshow', '1');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}
